import type { Coupon } from '@erp/shared';
import { Ban, RotateCcw, Pencil, Ticket } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import {
  Button,
  confirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { formatDate } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';

export function CouponsPage(): JSX.Element {
  const { t, i18n } = useTranslation('coupons');
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const canWrite = usePermission('coupons.write');
  const [coupons, setCoupons] = useState<Coupon[] | null>(null);

  const reload = async (): Promise<void> => {
    const r = await api().get<{ items: Coupon[] }>('/coupons');
    setCoupons(r.data.items);
  };

  useEffect(() => {
    void reload();
  }, []);

  if (!coupons) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  const onDeactivate = async (c: Coupon): Promise<void> => {
    if (!(await confirmDialog({ message: t('actions.confirmDeactivate', { code: c.code }), tone: 'danger' }))) return;
    try {
      await api().delete(`/coupons/${c.id}`);
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const onReactivate = async (c: Coupon): Promise<void> => {
    if (!(await confirmDialog({ message: t('actions.confirmReactivate', { code: c.code }) }))) return;
    try {
      await api().patch(`/coupons/${c.id}`, { isActive: true });
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const fmtDate = (s: string | null): string =>
    s ? formatDate(s, { locale: i18n.language }) : '…';

  const valueText = (c: Coupon): string =>
    c.discountType === 'percent'
      ? `${Number(c.discountValue)}%`
      : c.currencyCode
        ? formatMoney(c.discountValue, { locale: i18n.language, currency: c.currencyCode })
        : c.discountValue;

  const columns: DataTableColumn<Coupon>[] = [
    {
      id: 'code',
      header: t('list.columns.code'),
      sortValue: (c) => c.code,
      searchValue: (c) => c.code,
      cellClassName: 'font-mono text-xs',
      cell: (c) =>
        canWrite ? (
          <Link
            to={`/coupons/${c.id}/edit`}
            className="text-fg underline-offset-4 hover:text-brand hover:underline"
          >
            {c.code}
          </Link>
        ) : (
          c.code
        ),
    },
    {
      id: 'name',
      header: t('list.columns.name'),
      sortValue: (c) => c.name,
      searchValue: (c) => c.name,
      cellClassName: 'font-medium',
      cell: (c) => c.name,
    },
    {
      id: 'type',
      header: t('list.columns.type'),
      sortValue: (c) => c.discountType,
      cell: (c) => (
        <Badge variant={c.discountType === 'percent' ? 'info' : 'secondary'}>
          {t(`type.${c.discountType}`)}
        </Badge>
      ),
    },
    {
      id: 'value',
      header: t('list.columns.value'),
      align: 'end',
      sortValue: (c) => Number(c.discountValue),
      cellClassName: 'font-mono tabular-nums',
      cell: valueText,
    },
    {
      id: 'validity',
      header: t('list.columns.validity'),
      sortValue: (c) => c.endsAt ?? '',
      cell: (c) =>
        c.startsAt || c.endsAt ? (
          <span className="text-xs text-fg-muted">
            {fmtDate(c.startsAt)} → {fmtDate(c.endsAt)}
          </span>
        ) : (
          <span className="text-xs text-fg-subtle">{t('list.noExpiry')}</span>
        ),
    },
    {
      id: 'usage',
      header: t('list.columns.usage'),
      align: 'end',
      sortValue: (c) => c.usedCount,
      cellClassName: 'font-mono tabular-nums text-xs',
      cell: (c) => `${c.usedCount} / ${c.usageLimit != null ? c.usageLimit : t('list.unlimited')}`,
    },
    {
      id: 'status',
      header: t('list.columns.status'),
      sortValue: (c) => (c.isActive ? 1 : 0),
      cell: (c) => (
        <Badge variant={c.isActive ? 'success' : 'secondary'}>
          {t(`status.${c.isActive ? 'active' : 'inactive'}`)}
        </Badge>
      ),
    },
    ...(canWrite
      ? [
          {
            id: 'actions',
            header: '',
            align: 'end' as const,
            headClassName: 'w-28',
            cell: (c: Coupon) => (
              <div className="flex justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/coupons/${c.id}/edit`)}
                  aria-label={`${t('actions.edit')} ${c.code}`}
                  title={t('actions.edit')}
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                </Button>
                {c.isActive ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeactivate(c)}
                    aria-label={`${t('actions.deactivate')} ${c.code}`}
                    title={t('actions.deactivate')}
                  >
                    <Ban className="h-4 w-4" aria-hidden />
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onReactivate(c)}
                    aria-label={`${t('actions.reactivate')} ${c.code}`}
                    title={t('actions.reactivate')}
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden />
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('description')}</p>
        </div>
        {canWrite && <Button onClick={() => navigate('/coupons/new')}>{t('create')}</Button>}
      </header>

      {coupons.length === 0 ? (
        <EmptyState
          icon={<Ticket className="h-8 w-8" aria-hidden />}
          title={t('list.empty')}
          description={t('list.emptyDescription')}
          action={canWrite ? <Button onClick={() => navigate('/coupons/new')}>{t('create')}</Button> : undefined}
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={columns}
            data={coupons}
            rowKey={(c) => c.id}
            searchPlaceholder={t('searchPlaceholder')}
            rowClassName={(c) => (c.isActive ? undefined : 'opacity-60')}
          />
        </Card>
      )}
    </div>
  );
}
